/**
 * "Baixar exame e arquivos (ZIP)" (E196.F1, GUS-260): o exame e todos os arquivos recebidos no caso,
 * com o manifesto de cada recebimento, num ZIP só, antes da assinatura. Fica ao lado da prévia do
 * exame na mesa (card "Preparação do pacote") e na conferência final.
 *
 * O download entra na trilha também como abertura da prévia do exame: na conferência final,
 * `onBaixado` libera o "Marcar como revisado", como "Abrir a prévia" já faz.
 *
 * E205.F2 (ADR-0147 §2): na mesa, é um item do botão "Exame" (`useBaixarExameEArquivos`), com a falha
 * dita abaixo da barra (`FalhaDoZip`).
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { salvarArquivoBaixado } from '@/lib/http_download';
import { baixarExameEArquivos, PacoteDoCasoError } from '@/lib/preparacao_do_pacote';

const t = ptBR.preparacaoDoPacote;

export type FalhaDoZip = 'erro' | 'atualizando' | 'arquivos' | null;

export interface BaixarExameEArquivosProps {
  client: AxiosInstance;
  caseId: string;
  /** O ZIP foi salvo: a prévia do exame conta como aberta. */
  onBaixado?: (() => void) | undefined;
  /** A prévia está sendo refeita: quem mostra o card relê. */
  onAtualizar?: (() => void) | undefined;
}

function textoDaFalha(falha: Exclude<FalhaDoZip, null>): string {
  if (falha === 'atualizando') return t.avisoDeAtualizacao;
  if (falha === 'arquivos') return t.exameEArquivos.arquivos;
  return t.exameEArquivos.erro;
}

export interface DownloadDoZip {
  baixar: () => void;
  baixando: boolean;
  falha: FalhaDoZip;
}

export function useBaixarExameEArquivos(
  client: AxiosInstance,
  caseId: string,
  onBaixado?: () => void,
  onAtualizar?: () => void,
): DownloadDoZip {
  const [baixando, setBaixando] = useState(false);
  const [falha, setFalha] = useState<FalhaDoZip>(null);
  const baixar = useCallback((): void => {
    void (async () => {
      setBaixando(true);
      setFalha(null);
      try {
        salvarArquivoBaixado(await baixarExameEArquivos(client, caseId));
        onBaixado?.();
      } catch (err) {
        const motivo = err instanceof PacoteDoCasoError ? err.motivo : 'indisponivel';
        const tipo: Exclude<FalhaDoZip, null> =
          motivo === 'atualizando' || motivo === 'arquivos' ? motivo : 'erro';
        setFalha(tipo);
        if (tipo === 'atualizando') onAtualizar?.();
      } finally {
        setBaixando(false);
      }
    })();
  }, [client, caseId, onBaixado, onAtualizar]);
  return { baixar, baixando, falha };
}

export function FalhaDoZipDito({ falha }: { falha: FalhaDoZip }): JSX.Element | null {
  if (falha === null) return null;
  return (
    <p
      role={falha === 'atualizando' ? 'status' : 'alert'}
      className={`text-body-sm ${falha === 'atualizando' ? 'text-accent-700' : 'text-danger-700'}`}
      data-testid="baixar-exame-e-arquivos-falha"
    >
      {textoDaFalha(falha)}
    </p>
  );
}

export function BaixarExameEArquivos({
  client,
  caseId,
  onBaixado,
  onAtualizar,
}: BaixarExameEArquivosProps): JSX.Element {
  const { baixar, baixando, falha } = useBaixarExameEArquivos(
    client,
    caseId,
    onBaixado,
    onAtualizar,
  );
  return (
    <div className="space-y-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={baixando}
        onClick={baixar}
        title={t.exameEArquivos.dica}
        data-testid="baixar-exame-e-arquivos"
      >
        {baixando ? t.baixando : t.exameEArquivos.baixar}
      </Button>
      <FalhaDoZipDito falha={falha} />
    </div>
  );
}
